// "COI Pipeline" panel on the System Status board (WO A3 + cutover workorder §2.3 W4, ADR-014).
// READ-ONLY observability split into two clearly separated lanes:
//   Legacy (Airtable · shadow) — the original cards pulled from coi-api /queue-health +
//     /status-summary via GET /api/admin/coi/status. Airtable stays authoritative here.
//   New (webapp DB)            — what coi-api has dual-written into coi_records/coi_events via
//     POST /api/admin/coi/ingest, read from GET /api/admin/coi/new-lane. Shadow semantics:
//     rows are "would-have-written" — nothing here was applied to Airtable/Prophy.
// A reconciliation strip compares the two ("New N · Legacy M · Δ") so you can watch the new
// system fill up. No action buttons by design — no "run sweep", no apply-flag flips, no
// recheck (shadow-mode guardrail).
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";
import { COI_BLOCKED_VERDICTS, type CoiEventView, type CoiNewLaneView, type CoiRecordVerdict } from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { useCan } from "../../components/Can.js";
import { Badge, Card, CardBody, CardHeader, EmptyState, Spinner } from "../../components/ui.js";
import { Dot, relativeAge, type Health } from "./health.js";

// Mirrors CoiAdminStatus in apps/api/src/services/coi/backendStatus.ts (the wire contract of
// GET /api/admin/coi/status) — apps/web cannot import from apps/api, so it is restated here.
type CoiHealth = Health;

interface CoiAdminStatus {
  configured: boolean;
  reachable: boolean;
  /** True when the payload is last-known data from a previous successful pull. */
  stale: boolean;
  fetchedAt: string | null;
  health: CoiHealth;
  healthReasons: string[];
  heartbeat: {
    status: string;
    lastRunAt: string | null;
    lastSuccessAt: string | null;
    freshnessSloMins: number;
    health: CoiHealth;
  } | null;
  service: {
    name: string;
    revision: string | null;
    image: string | null;
    shadow: boolean;
    coiApply: boolean;
    proposalsApply: boolean;
    hasSession: boolean;
  } | null;
  queue: {
    total: number;
    fresh: number;
    overdue: number;
    blocked: number;
    blockedRecent: number;
    blockedStale: number;
    legacy: number;
    structuralFloor: number;
    verdict: string;
    sampledAt: string;
    growthPerDay: number | null;
    prevDelta: number | null;
    thresholds: Record<string, number>;
  } | null;
  scheduler: {
    job: string | null;
    schedule: string | null;
    state: string | null;
    taskQueue: {
      name?: string | null;
      state?: string | null;
      rate_limits?: Record<string, number> | null;
      retry_config?: Record<string, number> | null;
    } | null;
    recentEnqueues: Array<{
      at: string | null;
      status: string;
      queued: number | null;
      tasksCreated: number | null;
      /** Shadow-mode sweeps typically show tasks 0 but proposalTasks > 0 — not a no-op. */
      proposalTasksCreated: number | null;
      errors: number | null;
      latencyMs: number | null;
    }>;
  } | null;
  history: Array<{ at: string; total: number }>;
}

function verdictTone(verdict: string): "green" | "amber" | "red" | "slate" {
  if (verdict === "OK") return "green";
  if (verdict === "ATTENTION" || verdict === "INVESTIGATE") return "amber";
  if (verdict === "STALLED") return "red";
  return "slate";
}

/** New-lane record verdict → badge tone. Honest-state split (PRD): `pending` is neutral
 *  (in-flight, not alarming), no_match is structural-but-triageable (amber), auth/engine
 *  errors are red. Never collapse "checking" into "stuck". */
const RECORD_VERDICT_TONE: Record<CoiRecordVerdict, "green" | "amber" | "red" | "slate"> = {
  clear: "green",
  flag: "amber",
  reject: "red",
  no_match: "amber",
  auth_error: "red",
  error: "red",
  pending: "slate",
};

/** Display order for the verdict count pills. */
const VERDICT_ORDER: CoiRecordVerdict[] = ["clear", "flag", "reject", "no_match", "auth_error", "error", "pending"];

const signed = (n: number): string => (n >= 0 ? `+${n}` : `${n}`);

/** Hand-rolled sparkline of queue totals: a flex row of 3px bars, height scaled min→max. */
function Sparkline({ history, label }: { history: Array<{ at: string; total: number }>; label: string }): JSX.Element | null {
  if (history.length < 2) return null;
  const totals = history.map((h) => h.total);
  const min = Math.min(...totals);
  const max = Math.max(...totals);
  const range = max - min || 1;
  return (
    <div className="flex h-8 items-end gap-px" aria-label={label}>
      {history.map((h, i) => (
        <div
          key={`${h.at}-${i}`}
          className="w-[3px] rounded-sm bg-slate-400"
          // Floor at 15% so a flat series still renders visible bars.
          style={{ height: `${15 + ((h.total - min) / range) * 85}%` }}
          title={`${h.at} — total ${h.total}`}
        />
      ))}
    </div>
  );
}

/** Compact "k: v" rendering for the task-queue rate-limit / retry-config maps. */
function kvText(record: Record<string, number> | null | undefined): string {
  if (!record) return "";
  return Object.entries(record)
    .map(([k, v]) => `${k}: ${v}`)
    .join(" · ");
}

// ── Lane scaffolding (mockup component language: panel sections, tab strip) ──

function LaneSection({
  title,
  badge,
  children,
}: {
  title: string;
  badge: JSX.Element;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="overflow-hidden rounded-lg border border-slate-200">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2">
        <h3 className="text-xs font-semibold text-slate-700">{title}</h3>
        {badge}
      </div>
      <div className="px-3 py-3">{children}</div>
    </section>
  );
}

type NewLaneTab = "blocked" | "errors" | "records";

const tabId = (key: NewLaneTab): string => `coi-newlane-tab-${key}`;
const TABPANEL_ID = "coi-newlane-tabpanel";

/** Mockup-style tab strip (.tabbar/.tab): 2px accent underline on the active tab, inline
 *  severity-tinted counts. Full WAI-ARIA tabs pattern: roving tabindex, Arrow/Home/End keys,
 *  tabs point at the shared tabpanel below. */
function TabBar({
  tabs,
  active,
  onSelect,
}: {
  tabs: Array<{ key: NewLaneTab; label: string; count: number; countTone: "red" | "amber" | "slate" }>;
  active: NewLaneTab;
  onSelect: (key: NewLaneTab) => void;
}): JSX.Element {
  const countColor = { red: "text-red-600", amber: "text-amber-600", slate: "text-slate-400" } as const;
  const selectAndFocus = (key: NewLaneTab | undefined): void => {
    if (!key) return;
    onSelect(key);
    document.getElementById(tabId(key))?.focus();
  };
  const onKeyDown = (e: React.KeyboardEvent): void => {
    const i = tabs.findIndex((t) => t.key === active);
    if (e.key === "ArrowRight") selectAndFocus(tabs[(i + 1) % tabs.length]?.key);
    else if (e.key === "ArrowLeft") selectAndFocus(tabs[(i - 1 + tabs.length) % tabs.length]?.key);
    else if (e.key === "Home") selectAndFocus(tabs[0]?.key);
    else if (e.key === "End") selectAndFocus(tabs[tabs.length - 1]?.key);
    else return;
    e.preventDefault();
  };
  return (
    <div
      className="flex gap-1 overflow-x-auto border-b border-slate-200"
      role="tablist"
      aria-label="New lane drill-down"
      onKeyDown={onKeyDown}
    >
      {tabs.map((t) => (
        <button
          key={t.key}
          id={tabId(t.key)}
          type="button"
          role="tab"
          aria-selected={active === t.key}
          aria-controls={TABPANEL_ID}
          tabIndex={active === t.key ? 0 : -1}
          onClick={() => onSelect(t.key)}
          className={clsx(
            "whitespace-nowrap border-b-2 px-3 py-1.5 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-brand",
            active === t.key
              ? "border-brand font-medium text-slate-900"
              : "border-transparent text-slate-500 hover:text-slate-700",
          )}
        >
          {t.label}
          {t.count > 0 && <span className={clsx("ml-1 font-medium", countColor[t.countTone])}>{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

/** Compact single-line rendering of an event's detail JSON, truncated so a huge structlog
 *  payload can't blow up the row. */
function detailText(detail: Record<string, unknown>): string {
  const s = JSON.stringify(detail);
  return s.length > 200 ? `${s.slice(0, 200)}…` : s;
}

/** One coi_records row: identity, verdict pill, age; optional expandable event timeline. */
function RecordRow({
  record,
  events,
}: {
  record: CoiNewLaneView["blocked"][number];
  events?: CoiEventView[];
}): JSX.Element {
  return (
    <li className="border-t border-slate-100 py-2 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="text-sm font-medium">{record.reviewerName ?? record.reviewerEmail}</span>
          {record.reviewerName && <span className="ml-1.5 text-xs text-slate-500">{record.reviewerEmail}</span>}
          <p className="mt-0.5 font-mono text-xs text-slate-500">
            origin {record.preprintOriginId}
            {record.manuscriptId ? ` · ms ${record.manuscriptId}` : ""}
            {record.airtableRecordId ? ` · ${record.airtableRecordId}` : ""}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {record.shadow && <Badge tone="amber">shadow</Badge>}
          <Badge tone={RECORD_VERDICT_TONE[record.verdict]}>{record.verdict}</Badge>
          <span className="text-xs text-slate-400">{relativeAge(record.updatedAt)}</span>
        </div>
      </div>
      {events && events.length > 0 && (
        <details className="mt-1">
          <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700">
            timeline · {events.length} event{events.length > 1 ? "s" : ""}
          </summary>
          <ul className="mt-1 space-y-0.5 rounded-md bg-slate-50 px-2 py-1.5">
            {events.map((e) => (
              <li key={e.id} className="flex flex-wrap items-baseline gap-2 font-mono text-xs text-slate-500">
                <span className="w-16 shrink-0 text-slate-400">{relativeAge(e.ts)}</span>
                <span>{e.event}</span>
                {e.detail && Object.keys(e.detail).length > 0 && (
                  <span className="break-all text-slate-400">{detailText(e.detail)}</span>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </li>
  );
}

// ── New (webapp DB) lane body ─────────────────────────────────────────────────

function NewLane({ data }: { data: CoiNewLaneView }): JSX.Element {
  const [tab, setTab] = useState<NewLaneTab>("records");

  if (!data.available) {
    // Honest state: a failed read is "unavailable", never fake zeros.
    return <EmptyState>New lane unavailable — webapp DB read failed. Showing nothing rather than zeros.</EmptyState>;
  }

  // Tab counts come from the untruncated totals — the row lists below are capped at the
  // server's page size and must not masquerade as totals (honest-state).
  const blockedCount = COI_BLOCKED_VERDICTS.reduce((n, v) => n + (data.totals.byVerdict[v] ?? 0), 0);
  const errorCount = (data.totals.byVerdict.error ?? 0) + data.errors.events.length;

  return (
    <div className="space-y-3">
      {/* ── Verdict counts + shadow note ─────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-semibold text-slate-600">Records</span>
        <Badge tone="slate">total {data.totals.records}</Badge>
        {VERDICT_ORDER.filter((v) => (data.totals.byVerdict[v] ?? 0) > 0).map((v) => (
          <Badge key={v} tone={RECORD_VERDICT_TONE[v]}>
            {v} {data.totals.byVerdict[v]}
          </Badge>
        ))}
        <span className="text-xs text-slate-400">fetched {relativeAge(data.fetchedAt)}</span>
      </div>
      <p className="text-xs text-slate-500">
        {data.totals.shadow} of {data.totals.records} written in shadow — verdicts recorded here were{" "}
        <span className="font-medium">not applied to Airtable</span>.
      </p>
      {data.samples.length >= 2 && (
        <Sparkline history={data.samples.map((s) => ({ at: s.ts, total: s.total }))} label="New-lane record count history" />
      )}

      {/* ── Drill-down (SPEC §5, sourced from coi_records/coi_events) ─── */}
      <TabBar
        tabs={[
          { key: "records", label: "Records", count: data.totals.records, countTone: "slate" },
          { key: "blocked", label: "Blocked", count: blockedCount, countTone: "red" },
          { key: "errors", label: "Errors", count: errorCount, countTone: "amber" },
        ]}
        active={tab}
        onSelect={setTab}
      />

      <div role="tabpanel" id={TABPANEL_ID} aria-labelledby={tabId(tab)}>
        {tab === "records" &&
          (data.recent.length === 0 ? (
            <EmptyState>No records yet — the New lane fills as coi-api sweeps run (shadow writes).</EmptyState>
          ) : (
            <>
              <ul>
                {data.recent.map((r) => (
                  <RecordRow key={r.id} record={r} events={r.events} />
                ))}
              </ul>
              {data.totals.records > data.recent.length && (
                <p className="mt-1 text-xs text-slate-400">
                  showing latest {data.recent.length} of {data.totals.records}
                </p>
              )}
            </>
          ))}

        {tab === "blocked" && (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">
              Structurally stuck — the engine ran but cannot proceed (no Prophy author match / auth failure). Distinct
              from <span className="font-medium">pending</span>, which is just in-flight.
            </p>
            {blockedCount === 0 ? (
              <EmptyState>Nothing blocked in the new lane.</EmptyState>
            ) : (
              <>
                <ul>
                  {data.blocked.map((r) => (
                    <RecordRow key={r.id} record={r} />
                  ))}
                </ul>
                {blockedCount > data.blocked.length && (
                  <p className="mt-1 text-xs text-slate-400">
                    showing latest {data.blocked.length} of {blockedCount}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {tab === "errors" && (
          <div className="space-y-2">
            {errorCount === 0 ? (
              <EmptyState>No errors recorded in the new lane.</EmptyState>
            ) : (
              <>
                {data.errors.records.length > 0 && (
                  <>
                    <ul>
                      {data.errors.records.map((r) => (
                        <RecordRow key={r.id} record={r} />
                      ))}
                    </ul>
                    {(data.totals.byVerdict.error ?? 0) > data.errors.records.length && (
                      <p className="mt-1 text-xs text-slate-400">
                        showing latest {data.errors.records.length} of {data.totals.byVerdict.error} error records
                      </p>
                    )}
                  </>
                )}
                {data.errors.events.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-600">Recent error events</p>
                    <ul className="mt-1 space-y-0.5 rounded-md border border-slate-200 px-2 py-1.5">
                      {data.errors.events.map((e) => (
                        <li key={e.id} className="flex flex-wrap items-baseline gap-2 font-mono text-xs text-slate-500">
                          <span className="w-16 shrink-0 text-slate-400">{relativeAge(e.ts)}</span>
                          <span className="text-red-600">{e.event}</span>
                          {e.detail && Object.keys(e.detail).length > 0 && (
                            <span className="break-all text-slate-400">{detailText(e.detail)}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Ops runbook sidebar (COI ops UX) ──────────────────────────────────────────
// Same remediation copy as `just queue-status` (rrid-prophy-coi infra/gcp/queue-status.sh)
// and SRE RB-4 — keep the three in sync. Auto-expands when the queue is actually stuck
// (STALLED/INVESTIGATE or no Prophy session); collapsible otherwise.

const REMEDIATION_STEPS: Array<{ cmd: string; note: string }> = [
  { cmd: "extension → coi-token → GSM", note: "push a fresh Prophy session (log in at prophy.ai first)" },
  { cmd: "just cookie-refresh", note: "GSM → VM /home/jason_yan/prophy_cookies.json" },
  { cmd: "just legacy-logs 40 follow", note: "expect Loaded cookies… then /custom 200" },
  { cmd: "just queue-status", note: "overdue should fall" },
];

function CoiOpsRunbook({ triggered }: { triggered: boolean }): JSX.Element {
  const can = useCan();
  const canReveal = can("revealCoiExtensionSecret");
  const [copyState, setCopyState] = useState<"idle" | "working" | "copied" | "error">("idle");
  const [copyError, setCopyError] = useState<string | null>(null);

  const copySecret = async (): Promise<void> => {
    setCopyState("working");
    setCopyError(null);
    try {
      // One-time reveal (ops/admin only, audit-logged server-side). Straight to the
      // clipboard — deliberately never rendered on screen or cached in React Query.
      const { secret } = await api.post<{ secret: string }>("/admin/secrets/coi-token-webhook", {});
      await navigator.clipboard.writeText(secret);
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 8000);
    } catch (err) {
      setCopyState("error");
      setCopyError(err instanceof Error ? err.message : "reveal failed");
    }
  };

  return (
    <details
      open={triggered}
      className={clsx(
        "rounded-lg border px-3 py-2",
        triggered ? "border-amber-300 bg-amber-50" : "border-slate-200",
      )}
    >
      <summary className="cursor-pointer text-xs font-semibold text-slate-700">
        Ops runbook — clearing Airtable overdue{triggered ? " (action needed)" : ""}
      </summary>
      <div className="mt-2 space-y-2 text-xs text-slate-600">
        <p>
          Until cutover, <span className="font-medium">only the legacy VM writer clears Airtable overdue</span>.
          Cloud Run shadow checks (COI_APPLY=false) never write Airtable — a green session here does not drain
          the queue. From <span className="font-mono">rrid-prophy-coi</span>:
        </p>
        <p className="text-slate-500">
          First time on this machine? Set up the extension first (clone repo, spa-rrid-dev Chrome
          profile, Load unpacked):{" "}
          <a
            href="https://github.com/rrid-ucb/rrid-prophy-coi#token-refresh-no-monthly-cloud-run-ritual"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-blue-700 underline"
          >
            extension install &amp; setup (README)
          </a>
          . The steps below assume the extension is already installed.
        </p>
        <ol className="list-decimal space-y-1 pl-5">
          {REMEDIATION_STEPS.map((s) => (
            <li key={s.cmd}>
              <span className="font-mono font-medium text-slate-700">{s.cmd}</span>
              <span className="text-slate-500"> — {s.note}</span>
            </li>
          ))}
        </ol>
        <p className="text-slate-500">
          If <span className="font-mono">/custom</span> still 403s with a fresh cookie, it is Prophy
          folder/account access — grant access in Prophy org admin instead of refreshing again
          (runbook: rrid-gcp docs/ops-handbook/runbooks/prophy-cookie-refresh.md · SRE RB-4).
        </p>
        {canReveal ? (
          <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 pt-2">
            <button
              type="button"
              onClick={() => void copySecret()}
              disabled={copyState === "working"}
              className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {copyState === "working" ? "Revealing…" : "Copy extension signing secret"}
            </button>
            {copyState === "copied" && (
              <span className="text-green-700">Copied — paste into the extension options. Shown once; audit-logged.</span>
            )}
            {copyState === "error" && <span className="text-red-600">{copyError ?? "reveal failed"}</span>}
          </div>
        ) : (
          <p className="border-t border-slate-200 pt-2 text-slate-400">
            Extension signing secret: ask an ops/admin user, or run{" "}
            <span className="font-mono">just copy-webhook-secret</span> in rrid-prophy-coi.
          </p>
        )}
      </div>
    </details>
  );
}

// ── Panel ─────────────────────────────────────────────────────────────────────

export function CoiPanel(): JSX.Element | null {
  const can = useCan();
  const allowed = can("viewAuditTrail");
  const { data, isLoading, isError } = useQuery({
    queryKey: qk.adminCoiStatus,
    queryFn: () => api.get<CoiAdminStatus>("/admin/coi/status"),
    enabled: allowed,
    refetchInterval: 30000,
  });
  const newLane = useQuery({
    queryKey: qk.adminCoiNewLane,
    queryFn: () => api.get<CoiNewLaneView>("/admin/coi/new-lane"),
    enabled: allowed,
    refetchInterval: 30000,
  });

  if (!allowed) return null; // AdminDashboard already gates; belt-and-suspenders.

  const newTotal = newLane.data?.available ? newLane.data.totals.records : null;
  const legacyTotal = data?.queue?.total ?? null;
  // Auto-expand the ops runbook when the queue is stuck, the session is gone, or the
  // backend can't be reached live — exactly the states where the instructions matter.
  const runbookTriggered =
    !!data &&
    (["STALLED", "INVESTIGATE"].includes(data.queue?.verdict ?? "") ||
      (data.service != null && !data.service.hasSession) ||
      (data.configured && !data.reachable));

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold">COI Pipeline</h2>
          {data && <Dot health={data.health} />}
          {data?.stale && <Badge tone="amber">stale — showing last-known</Badge>}
          {data && !data.reachable && data.configured && <Badge tone="red">backend unreachable</Badge>}
          {data?.fetchedAt && <span className="text-xs text-slate-400">fetched {relativeAge(data.fetchedAt)}</span>}
        </div>
        {data && data.healthReasons.length > 0 && (
          <p className="mt-0.5 text-xs text-slate-500">{data.healthReasons.join(" · ")}</p>
        )}
        {data?.stale && (
          <p className="mt-0.5 text-xs text-amber-700">
            Stale means the dashboard cannot reach coi-api right now — the Legacy lane below shows
            the last successful pull ({relativeAge(data.fetchedAt)}), not live values. It usually
            self-heals within a minute; if it persists, check coi-api logs for OOM/restarts.
          </p>
        )}
        {/* ── Reconciliation strip (workorder §2.3): dual-write fill, NOT stall health ──
            Δ = new − legacy. Empty new lane vs large Airtable depth is expected until
            coi-api ingests verdicts. Stall signal is overdue / STALLED on the Legacy lane. */}
        {newTotal != null && (
          <div className="mt-1.5 space-y-0.5 text-xs text-slate-600">
            <p>
              <span className="font-semibold">Reconciliation:</span> New lane has{" "}
              <span className="font-medium">{newTotal}</span> record{newTotal === 1 ? "" : "s"}
              {legacyTotal != null ? (
                <>
                  {" "}
                  · Airtable queue has <span className="font-medium">{legacyTotal}</span> · Δ{" "}
                  <span
                    className={clsx(
                      "font-medium",
                      newTotal - legacyTotal === 0 ? "text-green-700" : "text-slate-700",
                    )}
                  >
                    {signed(newTotal - legacyTotal)}
                  </span>
                  <span className="text-slate-400"> (new−legacy; not a stall signal)</span>
                </>
              ) : isLoading ? (
                <> · Legacy queue still loading…</>
              ) : data && !data.configured ? (
                <> · Legacy backend not configured</>
              ) : data && !data.reachable ? (
                <> · Legacy backend not reachable</>
              ) : (
                <> · Legacy queue depth unavailable</>
              )}
            </p>
            {newTotal === 0 && (
              <p className="text-slate-400">
                Empty new lane is expected until dual-write ingest runs. Watch{" "}
                <span className="font-medium text-slate-600">overdue / STALLED</span> below for
                pipeline health.
              </p>
            )}
          </div>
        )}
      </CardHeader>
      <CardBody className="space-y-4">
        {/* ════ Ops runbook — on-screen remediation for stuck queues ════ */}
        <CoiOpsRunbook triggered={runbookTriggered} />

        {/* ════ Legacy lane (Airtable · shadow) — original cards ════ */}
        <LaneSection title="Legacy — Airtable" badge={<Badge tone="amber">shadow</Badge>}>
          {isLoading ? (
            <Spinner label="Loading COI status…" />
          ) : isError || !data ? (
            <EmptyState>Could not load COI status.</EmptyState>
          ) : !data.configured ? (
            <EmptyState>COI backend not configured (local).</EmptyState>
          ) : (
            <div className="space-y-3">
              {data.heartbeat && (
                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                  <Dot health={data.heartbeat.health} />
                  <span>
                    heartbeat {data.heartbeat.status} · last success {relativeAge(data.heartbeat.lastSuccessAt)} · last
                    run {relativeAge(data.heartbeat.lastRunAt)} · SLO ≤ {data.heartbeat.freshnessSloMins}m
                  </span>
                </div>
              )}

              {/* ── Service ─────────────────────────────────────────────── */}
              {data.service && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{data.service.name}</span>
                      {data.service.shadow ? <Badge tone="amber">SHADOW</Badge> : <Badge tone="green">LIVE</Badge>}
                    </div>
                    <p className="mt-0.5 font-mono text-xs text-slate-500">
                      {data.service.revision ?? "revision unknown"}
                      {data.service.image ? ` · ${data.service.image}` : ""}
                    </p>
                    <p className="text-xs text-slate-500">
                      coi_apply {String(data.service.coiApply)} · proposals_apply {String(data.service.proposalsApply)}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                    <span
                      className={`inline-block h-2 w-2 rounded-full ${data.service.hasSession ? "bg-green-500" : "bg-red-500"}`}
                      aria-hidden
                    />
                    {data.service.hasSession ? "Prophy session" : "no session"}
                  </span>
                </div>
              )}

              {/* ── Queue ───────────────────────────────────────────────── */}
              {data.queue && (
                <div className="space-y-1.5 border-t border-slate-100 pt-3">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs font-semibold text-slate-600">Queue</span>
                    <Badge tone={verdictTone(data.queue.verdict)}>{data.queue.verdict}</Badge>
                    <span className="text-xs text-slate-400">sampled {relativeAge(data.queue.sampledAt)}</span>
                    {!data.reachable && <Badge tone="amber">cached</Badge>}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge tone="green">fresh {data.queue.fresh}</Badge>
                    <Badge tone="amber">overdue {data.queue.overdue}</Badge>
                    <Badge tone="red">blocked recent {data.queue.blockedRecent}</Badge>
                    <Badge tone="red">blocked stale {data.queue.blockedStale}</Badge>
                    <Badge tone="slate">legacy {data.queue.legacy}</Badge>
                  </div>
                  <p className="text-xs text-slate-500">
                    total {data.queue.total} (structural floor {data.queue.structuralFloor})
                  </p>
                  {(data.queue.growthPerDay != null || data.queue.prevDelta != null) && (
                    <p className="text-xs text-slate-500">
                      {data.queue.growthPerDay != null && <>growth/day: {signed(data.queue.growthPerDay)}</>}
                      {data.queue.growthPerDay != null && data.queue.prevDelta != null && " · "}
                      {data.queue.prevDelta != null && <>Δ vs prev sample: {signed(data.queue.prevDelta)}</>}
                    </p>
                  )}
                  <Sparkline history={data.history} label="Queue total history" />
                </div>
              )}

              {/* ── Scheduler ───────────────────────────────────────────── */}
              {data.scheduler && (
                <div className="space-y-1.5 border-t border-slate-100 pt-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-slate-600">Scheduler</span>
                    {data.scheduler.job && <span className="font-mono text-xs text-slate-600">{data.scheduler.job}</span>}
                    {data.scheduler.schedule && (
                      <span className="font-mono text-xs text-slate-500">{data.scheduler.schedule}</span>
                    )}
                    {data.scheduler.state && <Badge tone="slate">{data.scheduler.state}</Badge>}
                  </div>
                  {data.scheduler.taskQueue && (
                    <p className="text-xs text-slate-500">
                      {data.scheduler.taskQueue.name && <>queue {data.scheduler.taskQueue.name}</>}
                      {data.scheduler.taskQueue.state && <> · {data.scheduler.taskQueue.state}</>}
                      {kvText(data.scheduler.taskQueue.rate_limits) && (
                        <> · rate limits — {kvText(data.scheduler.taskQueue.rate_limits)}</>
                      )}
                      {kvText(data.scheduler.taskQueue.retry_config) && (
                        <> · retry — {kvText(data.scheduler.taskQueue.retry_config)}</>
                      )}
                    </p>
                  )}
                  <p className="text-xs font-semibold text-slate-600">Recent /enqueue outcomes</p>
                  {data.scheduler.recentEnqueues.length === 0 ? (
                    <p className="text-xs text-slate-400">No enqueues observed by this instance yet.</p>
                  ) : (
                    <ul className="space-y-1">
                      {data.scheduler.recentEnqueues.map((e, i) => (
                        <li
                          key={`${e.at ?? "?"}-${i}`}
                          className="flex flex-wrap items-center gap-2 text-xs text-slate-500"
                        >
                          <span className="w-16 shrink-0">{relativeAge(e.at)}</span>
                          <Badge tone={e.status === "ok" ? "green" : "red"}>{e.status}</Badge>
                          <span>
                            queued {e.queued ?? "—"} · tasks {e.tasksCreated ?? "—"} · proposal tasks{" "}
                            {e.proposalTasksCreated ?? "—"} · errors {e.errors ?? "—"}
                            {e.latencyMs != null && ` · ${e.latencyMs}ms`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </LaneSection>

        {/* ════ New lane (webapp DB) — dual-written by coi-api ════ */}
        <LaneSection title="New — webapp DB" badge={<Badge tone="blue">new · dev instance</Badge>}>
          {newLane.data ? (
            // A failed background refetch keeps the retained data on screen (stale-with-
            // last-known, like the legacy lane) instead of blanking a lane the header's
            // reconciliation strip still counts.
            <div className="space-y-2">
              {newLane.isError && <Badge tone="amber">refresh failing — showing last-known</Badge>}
              <NewLane data={newLane.data} />
            </div>
          ) : newLane.isLoading ? (
            <Spinner label="Loading new lane…" />
          ) : (
            <EmptyState>Could not load the new lane.</EmptyState>
          )}
        </LaneSection>
      </CardBody>
    </Card>
  );
}
